import {
    useMutation,
    UseMutationResult,
    useQuery,
    UseQueryResult,
} from 'react-query';
import { AxiosResponse } from 'axios';
import {
    withHeaders,
    instance as axiosInstance,
} from 'core/util/axiosInstance';
import { useAlert } from 'core/contexts';
import { commonShowAlert } from 'lib';
import { getDefaultHeaders } from 'core/util/other';
import { JAVASCRIPT_TRANSFORM } from 'pages/pipelines/pipelines-helper';

const BASE_JS_TRANSFORM_URL = '/metadata/datapipeline/steps/transform';

interface FetchJSTransformProps {
    id: string;
    token: string;
}

export interface JSTransformStepResponse {
    id: string;
    name: string;
    description: string;
    funcName: string;
    jsBase64: string;
    author: string;
    hash: string;
    timestamp: string;
    type: 'JS_TRANSFORM';
}

export interface PostJSTransformStepProps {
    name: string;
    description: string;
    funcName: string;
    jsBase64: string;
}

export interface PutJSTransformStepProps extends PostJSTransformStepProps {
    id: string;
}

interface CreateJSTransformStepProps {
    postData: PostJSTransformStepProps;
    token: string;
}

interface UpdateJSTransformStepProps {
    putData: PutJSTransformStepProps;
    token: string;
}

interface UpdateJSTransformTrialRunProps {
    id: string;
    token: string;
    postData: {};
}

export const getJSTransformData = async (
    url: string,
    token: string
): Promise<JSTransformStepResponse> => {
    const headers = getDefaultHeaders(token);
    const response = await axiosInstance().get(url, withHeaders(headers));
    return response.data;
};

export const useJSTransform = (
    props: FetchJSTransformProps,
    type: string
): UseQueryResult<JSTransformStepResponse, Error> => {
    const { id, token } = props;
    const { showAlert, hideAlert, showErrorDetails } = useAlert();
    const url = `${BASE_JS_TRANSFORM_URL}/${id}`;

    return useQuery(
        ['jsTransform', id],
        async () => getJSTransformData(url, token),
        {
            enabled: type === JAVASCRIPT_TRANSFORM,
            onError: (error: any) => {
                const errorMessage: string =
                    error?.data?.message ??
                    'Unable to retrieve javascript transform data';
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'error',
                    errorMessage,
                    JSON.stringify(error?.data ?? ''),
                    showErrorDetails
                );
            },
        }
    );
};

export const postJSTransform = (
    props: CreateJSTransformStepProps
): Promise<AxiosResponse<JSTransformStepResponse, any>> => {
    const { postData, token } = props;
    const url = `${BASE_JS_TRANSFORM_URL}`;

    return axiosInstance().post(
        url,
        postData,
        withHeaders(getDefaultHeaders(token))
    );
};

export const useCreateJSTransform = (): UseMutationResult<
    AxiosResponse<JSTransformStepResponse, any>,
    Error,
    CreateJSTransformStepProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(postJSTransform, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Step Created.');
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to create step';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const putJSTransform = (
    props: UpdateJSTransformStepProps
): Promise<AxiosResponse<JSTransformStepResponse, any>> => {
    const { putData, token } = props;
    const url = `${BASE_JS_TRANSFORM_URL}/${putData.id}`;

    return axiosInstance().put(
        url,
        putData,
        withHeaders(getDefaultHeaders(token))
    );
};

export const useUpdateJSTransform = (): UseMutationResult<
    AxiosResponse<JSTransformStepResponse, any>,
    Error,
    UpdateJSTransformStepProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(putJSTransform, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Step Updated');
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to update step';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const postJSTransformTrialRun = (
    props: UpdateJSTransformTrialRunProps
): Promise<AxiosResponse> => {
    const { id, postData, token } = props;
    const url = `${BASE_JS_TRANSFORM_URL}/${id}/trial-run`;

    return axiosInstance().post(
        url,
        postData,
        withHeaders(getDefaultHeaders(token))
    );
};

export const useJSTransformTrialRun = (): UseMutationResult<
    AxiosResponse<any, any>,
    any,
    UpdateJSTransformTrialRunProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(postJSTransformTrialRun, {
        onSuccess: () => {
            commonShowAlert(
                showAlert,
                hideAlert,
                'success',
                'Trial Run Created'
            );
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to create trial run';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const deleteJavaScriptTransform = (
    props: FetchJSTransformProps
): Promise<AxiosResponse<void, any>> => {
    const { id, token } = props;
    const url = `${BASE_JS_TRANSFORM_URL}/${id}`;

    return axiosInstance().delete(url, withHeaders(getDefaultHeaders(token)));
};

export const useDeleteJavaScriptTransform = (): UseMutationResult<
    AxiosResponse<any, any>,
    Error,
    FetchJSTransformProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(
        (props: FetchJSTransformProps) => deleteJavaScriptTransform(props),
        {
            onSuccess: () => {
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'success',
                    'Step Deleted'
                );
            },
            onError: (error: any) => {
                const errorMessage: string =
                    error?.data?.message ?? 'Unable to delete step';
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'error',
                    errorMessage,
                    JSON.stringify(error?.data ?? ''),
                    showErrorDetails
                );
            },
        }
    );
};
